import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { EditorMode } from '@/types/editor-context';
import type { AIJobListItem } from '@/types/ai';
import { resolveRestoredSplitTaskFields } from '@/lib/storyboard-image-split-restore';
import { useGenerationLimiterStore } from './generationLimiterStore';

export type GenerationSlotKey =
  | 'storyboardImage'
  | 'storyboardVideo'
  | 'characterImage'
  | 'locationImage'
  | 'propImage';

export type GenerationProgressPhase = 'progress' | 'finishing';

export interface ProcessingPreviewItem {
  id: string;
  /** 对应 GenerationTask.localTaskId，用于精确匹配（避免同 targetKey 多任务互相干扰） */
  localTaskId: string;
  targetKey: string;
  mode: EditorMode;
  assetType: 'image' | 'video';
  projectId: string;
  storyboardId: string | null;
  resourceId: string | null;
  tagId: string | null;
  previewItemId: string | null;
  title: string;
  description: string;
  type: 'storyboard' | 'character' | 'location' | 'prop' | 'video';
  thumbnailUrl: string;
  status: 'processing';
  progress: number;
  /** 用户在生图时选择的宽高比（宽/高），用于前端占位缩略图的正确比例渲染 */
  aspectRatio?: number;
  phase: GenerationProgressPhase;
  progressMessage?: string;
  estimatedRemainingSeconds?: number;
  pollIntervalMs?: number;
  /** 与后端任务 id 对齐，便于任务列表跳转时从 processingItems 反查 localTaskId */
  jobId?: string;
}

export interface GenerationTask {
  localTaskId: string;
  jobId?: string;
  slot: GenerationSlotKey;
  /** 组合 key = `${slot}:${storyboardId ?? resourceId ?? 'global'}`，用于 runningBySlot 操作 */
  slotKey: string;
  targetKey: string;
  mode: EditorMode;
  projectId: string;
  storyboardId: string | null;
  resourceId: string | null;
  tagId: string | null;
  previewItemId: string | null;
  status: 'processing' | 'finishing' | 'completed' | 'failed';
  createdAt: number;
  prompt: string;
  assetType: 'image' | 'video';
  previousAssetUrl: string | null;
  previousThumbnailUrl: string | null;
  progress: number;
  aspectRatio?: number;
  origin?: 'image-edit' | 'image-upscale' | 'image-split';
  /** 缺省或 true 时结束路径 decrementActive；image-edit / image-upscale / image-split 传 false */
  affectsLimiter?: boolean;
}

export type StartGenerationTaskPayload = {
  mode: EditorMode;
  projectId: string;
  storyboardId: string | null;
  resourceId: string | null;
  tagId: string | null;
  previewItemId: string | null;
  prompt: string;
  title: string;
  description: string;
  thumbnailUrl: string;
  previousAssetUrl?: string | null;
  previousThumbnailUrl?: string | null;
  type: ProcessingPreviewItem['type'];
  assetType: 'image' | 'video';
  aspectRatio?: number;
  origin?: 'image-edit' | 'image-upscale' | 'image-split';
  affectsLimiter?: boolean;
};

function shouldDecrementLimiter(task: GenerationTask): boolean {
  return task.affectsLimiter !== false;
}

export interface GeneratedAssetHistoryItem {
  id: string;
  targetKey: string;
  assetType: 'image' | 'video';
  url: string;
  thumbnailUrl: string;
  prompt: string;
  timestamp: number;
  tagId: string | null;
  previewItemId: string | null;
}

export interface GeneratedAssetCurrent {
  targetKey: string;
  assetType: 'image' | 'video';
  url: string;
  thumbnailUrl: string;
  prompt: string;
  completedAt: number;
  previewItemId: string | null;
  tagId: string | null;
  aspectRatio?: number;
}

const SLOT_BY_MODE: Record<EditorMode, GenerationSlotKey | null> = {
  'storyboard-image': 'storyboardImage',
  'storyboard-video': 'storyboardVideo',
  'character-image': 'characterImage',
  'location-image': 'locationImage',
  'prop-image': 'propImage',
  'dialogue-edit': null,
  'voice-generation': null,
  'sound-effect': null,
};

/** 构建 runningBySlot 的组合 key，按 storyboard/resource 隔离互斥 */
export function buildSlotKey(
  slot: GenerationSlotKey,
  context: { storyboardId?: string | null; resourceId?: string | null }
): string {
  return `${slot}:${context.storyboardId ?? context.resourceId ?? 'global'}`;
}

export function buildGenerationTargetKey(input: {
  mode: EditorMode;
  projectId: string;
  storyboardId: string | null;
  resourceId: string | null;
  tagId: string | null;
  previewItemId?: string | null;
}) {
  if (input.mode === 'storyboard-image' || input.mode === 'storyboard-video') {
    return `${input.mode}:${input.projectId}:${input.storyboardId ?? 'none'}:${input.tagId ?? 'none'}:${input.previewItemId ?? 'new'}`;
  }
  return `${input.mode}:${input.projectId}:${input.resourceId ?? 'none'}:${input.tagId ?? 'none'}`;
}

function isActiveGenerationTaskStatus(status: GenerationTask['status']) {
  return status === 'processing' || status === 'finishing';
}

interface GenerationState {
  /** key = `${slot}:${storyboardId ?? resourceId ?? 'global'}`，按分镜+类型隔离互斥 */
  runningBySlot: Record<string, string | null>;
  tasks: GenerationTask[];
  processingItems: ProcessingPreviewItem[];
  currentByTarget: Record<string, GeneratedAssetCurrent>;
  historyByTarget: Record<string, GeneratedAssetHistoryItem[]>;
  canSubmit: (
    mode: EditorMode,
    context?: { storyboardId?: string | null; resourceId?: string | null }
  ) => { allowed: boolean; reason?: string };
  startTask: (payload: StartGenerationTaskPayload) => {
    taskId: string;
    targetKey: string;
  } | null;
  bindJobId: (taskId: string, jobId: string) => void;
  updateTaskProgress: (
    taskId: string,
    payload: {
      progress: number;
      phase?: GenerationProgressPhase;
      progressMessage?: string;
      estimatedRemainingSeconds?: number;
      pollIntervalMs?: number;
    }
  ) => void;
  markTaskFinishing: (
    taskId: string,
    payload: {
      progress: number;
      progressMessage?: string;
      estimatedRemainingSeconds?: number;
      pollIntervalMs?: number;
    }
  ) => void;
  /**
   * sendMessage 成功后立即释放 slot 锁，允许同槽位下一次提交，
   * 同时保留 task 在 tasks[] 中继续轮询（status 不变）。
   */
  releaseTaskSlot: (taskId: string) => void;
  finishTask: (taskId: string) => void;
  finishTaskWithResult: (
    taskId: string,
    payload: {
      outputUrl: string;
      thumbnailUrl?: string | null;
    }
  ) => void;
  failTask: (taskId: string) => void;
  /** 撤回等场景：直接移除任务与占位，不残留 failed 态 */
  discardTask: (taskId: string) => void;
  restoreFromJobs: (jobs: AIJobListItem[], imageEditJobIds?: Iterable<string>) => void;
  removeProcessingByTarget: (targetKey: string) => void;
  removeCurrentByTarget: (targetKey: string) => void;
}

export const useGenerationStore = create<GenerationState>()(
  devtools(
    (set, get) => ({
      runningBySlot: {},
      tasks: [],
      processingItems: [],
      currentByTarget: {},
      historyByTarget: {},
      canSubmit: (mode, context) => {
        const slot = SLOT_BY_MODE[mode];
        if (!slot) return { allowed: false, reason: '当前模式不支持生成任务提交' };
        const slotKey = context ? buildSlotKey(slot, context) : slot;
        const runningId = get().runningBySlot[slotKey];
        if (runningId) {
          return { allowed: false, reason: '当前分镜已有相同类型任务在生成中，请等待完成后再提交' };
        }
        return { allowed: true };
      },
      startTask: (payload) => {
        const slot = SLOT_BY_MODE[payload.mode];
        if (!slot) return null;

        const requiresTag =
          payload.mode === 'character-image' ||
          payload.mode === 'location-image' ||
          payload.mode === 'prop-image';
        if (requiresTag && !payload.tagId) {
          if (import.meta.env.DEV) {
            console.warn('[generationStore.startTask] missing tagId for resource mode', {
              mode: payload.mode,
              projectId: payload.projectId,
              resourceId: payload.resourceId,
              previewItemId: payload.previewItemId,
            });
          }
          return null;
        }

        const slotKey = buildSlotKey(slot, {
          storyboardId: payload.storyboardId,
          resourceId: payload.resourceId,
        });

        if (get().runningBySlot[slotKey]) {
          return null;
        }

        const taskId = `task-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const targetKey = buildGenerationTargetKey(payload);
        const task: GenerationTask = {
          localTaskId: taskId,
          slot,
          slotKey,
          targetKey,
          mode: payload.mode,
          projectId: payload.projectId,
          storyboardId: payload.storyboardId,
          resourceId: payload.resourceId,
          tagId: payload.tagId,
          previewItemId: payload.previewItemId,
          status: 'processing',
          createdAt: Date.now(),
          prompt: payload.prompt,
          assetType: payload.assetType,
          previousAssetUrl: payload.previousAssetUrl ?? null,
          previousThumbnailUrl: payload.previousThumbnailUrl ?? null,
          progress: 0,
          aspectRatio: payload.aspectRatio,
          origin: payload.origin,
          affectsLimiter: payload.affectsLimiter,
        };

        const processingItem: ProcessingPreviewItem = {
          id: `processing-${taskId}`,
          localTaskId: taskId,
          targetKey,
          mode: payload.mode,
          assetType: payload.assetType,
          projectId: payload.projectId,
          storyboardId: payload.storyboardId,
          resourceId: payload.resourceId,
          tagId: payload.tagId,
          previewItemId: payload.previewItemId,
          title: payload.title,
          description: payload.description,
          type: payload.type,
          thumbnailUrl: payload.thumbnailUrl,
          status: 'processing',
          progress: 0,
          aspectRatio: payload.aspectRatio,
          phase: 'progress',
        };

        set((state) => ({
          runningBySlot: {
            ...state.runningBySlot,
            [slotKey]: taskId,
          },
          tasks: [task, ...state.tasks],
          processingItems: [processingItem, ...state.processingItems],
        }));

        return { taskId, targetKey };
      },
      bindJobId: (taskId, jobId) =>
        set((state) => ({
          tasks: state.tasks.map((task) =>
            task.localTaskId === taskId
              ? {
                  ...task,
                  jobId,
                }
              : task
          ),
          processingItems: state.processingItems.map((item) =>
            item.localTaskId === taskId ? { ...item, jobId } : item
          ),
        })),
      updateTaskProgress: (taskId, payload) =>
        set((state) => {
          const task = state.tasks.find((item) => item.localTaskId === taskId);
          if (!task || task.status !== 'processing') return state;
          const nextProgress = Math.max(0, Math.min(100, payload.progress));
          return {
            tasks: state.tasks.map((item) =>
              item.localTaskId === taskId
                ? {
                    ...item,
                    progress: nextProgress,
                  }
                : item
            ),
            processingItems: state.processingItems.map((item) =>
              item.localTaskId === taskId
                ? {
                    ...item,
                    progress: nextProgress,
                    phase: payload.phase ?? item.phase,
                    progressMessage: payload.progressMessage,
                    estimatedRemainingSeconds: payload.estimatedRemainingSeconds,
                    pollIntervalMs: payload.pollIntervalMs,
                  }
                : item
            ),
          };
        }),
      markTaskFinishing: (taskId, payload) =>
        set((state) => {
          const task = state.tasks.find((item) => item.localTaskId === taskId);
          if (!task || task.status !== 'processing') return state;
          const nextProgress = Math.max(0, Math.min(100, payload.progress));
          return {
            runningBySlot: {
              ...state.runningBySlot,
              [task.slotKey]:
                state.runningBySlot[task.slotKey] === taskId
                  ? null
                  : state.runningBySlot[task.slotKey],
            },
            tasks: state.tasks.map((item) =>
              item.localTaskId === taskId
                ? {
                    ...item,
                    status: 'finishing',
                    progress: nextProgress,
                  }
                : item
            ),
            processingItems: state.processingItems.map((item) =>
              item.localTaskId === taskId
                ? {
                    ...item,
                    progress: nextProgress,
                    phase: 'finishing',
                    progressMessage: payload.progressMessage,
                    estimatedRemainingSeconds: payload.estimatedRemainingSeconds,
                    pollIntervalMs: payload.pollIntervalMs,
                  }
                : item
            ),
          };
        }),
      releaseTaskSlot: (taskId) =>
        set((state) => {
          const task = state.tasks.find((item) => item.localTaskId === taskId);
          if (!task) return state;
          return {
            runningBySlot: {
              ...state.runningBySlot,
              [task.slotKey]:
                state.runningBySlot[task.slotKey] === taskId
                  ? null
                  : state.runningBySlot[task.slotKey],
            },
          };
        }),
      finishTask: (taskId) =>
        set((state) => {
          const task = state.tasks.find((item) => item.localTaskId === taskId);
          if (!task || !isActiveGenerationTaskStatus(task.status)) return state;
          if (shouldDecrementLimiter(task)) {
            useGenerationLimiterStore.getState().decrementActive();
          }
          return {
            runningBySlot: {
              ...state.runningBySlot,
              [task.slotKey]:
                state.runningBySlot[task.slotKey] === taskId
                  ? null
                  : state.runningBySlot[task.slotKey],
            },
            tasks: state.tasks.map((item) =>
              item.localTaskId === taskId
                ? {
                    ...item,
                    status: 'completed',
                  }
                : item
            ),
            processingItems: state.processingItems.filter((item) => item.localTaskId !== taskId),
          };
        }),
      finishTaskWithResult: (taskId, payload) =>
        set((state) => {
          const task = state.tasks.find((item) => item.localTaskId === taskId);
          if (!task || !isActiveGenerationTaskStatus(task.status)) return state;

          if (shouldDecrementLimiter(task)) {
            useGenerationLimiterStore.getState().decrementActive();
          }

          const historyRecord: GeneratedAssetHistoryItem[] = [];
          if (task.previousAssetUrl) {
            historyRecord.push({
              id: `history-${task.localTaskId}-${Date.now()}`,
              targetKey: task.targetKey,
              assetType: task.assetType,
              url: task.previousAssetUrl,
              thumbnailUrl: task.previousThumbnailUrl || task.previousAssetUrl,
              prompt: task.prompt,
              timestamp: Date.now(),
              tagId: task.tagId,
              previewItemId: task.previewItemId,
            });
          }

          return {
            runningBySlot: {
              ...state.runningBySlot,
              [task.slotKey]:
                state.runningBySlot[task.slotKey] === taskId
                  ? null
                  : state.runningBySlot[task.slotKey],
            },
            tasks: state.tasks.map((item) =>
              item.localTaskId === taskId
                ? {
                    ...item,
                    status: 'completed',
                  }
                : item
            ),
            processingItems: state.processingItems.filter((item) => item.localTaskId !== taskId),
            currentByTarget: {
              ...state.currentByTarget,
              [task.targetKey]: {
                targetKey: task.targetKey,
                assetType: task.assetType,
                url: payload.outputUrl,
                thumbnailUrl: payload.thumbnailUrl || payload.outputUrl,
                prompt: task.prompt,
                completedAt: Date.now(),
                previewItemId: task.previewItemId,
                tagId: task.tagId,
                aspectRatio: task.aspectRatio,
              },
            },
            historyByTarget: {
              ...state.historyByTarget,
              [task.targetKey]: [
                ...historyRecord,
                ...(state.historyByTarget[task.targetKey] ?? []),
              ],
            },
          };
        }),
      failTask: (taskId) =>
        set((state) => {
          const task = state.tasks.find((item) => item.localTaskId === taskId);
          if (!task || !isActiveGenerationTaskStatus(task.status)) return state;
          if (shouldDecrementLimiter(task)) {
            useGenerationLimiterStore.getState().decrementActive();
          }
          return {
            runningBySlot: {
              ...state.runningBySlot,
              [task.slotKey]:
                state.runningBySlot[task.slotKey] === taskId
                  ? null
                  : state.runningBySlot[task.slotKey],
            },
            tasks: state.tasks.map((item) =>
              item.localTaskId === taskId
                ? {
                    ...item,
                    status: 'failed',
                  }
                : item
            ),
            processingItems: state.processingItems.filter((item) => item.localTaskId !== taskId),
          };
        }),
      discardTask: (taskId) =>
        set((state) => {
          const task = state.tasks.find((item) => item.localTaskId === taskId);
          if (!task) return state;
          if (shouldDecrementLimiter(task)) {
            useGenerationLimiterStore.getState().decrementActive();
          }
          return {
            runningBySlot: {
              ...state.runningBySlot,
              [task.slotKey]:
                state.runningBySlot[task.slotKey] === taskId
                  ? null
                  : state.runningBySlot[task.slotKey],
            },
            tasks: state.tasks.filter((item) => item.localTaskId !== taskId),
            processingItems: state.processingItems.filter((item) => item.localTaskId !== taskId),
          };
        }),
      restoreFromJobs: (jobs, imageEditJobIds) => {
        const existingJobIds = new Set(
          get()
            .tasks.filter((t) => t.jobId)
            .map((t) => t.jobId)
        );

        const MODE_MAP: Record<string, EditorMode | null> = {
          '1_1': 'storyboard-image',
          '1_2': 'storyboard-video',
          '2_1': 'character-image',
          '3_1': 'location-image',
          '4_1': 'prop-image',
        };

        const TYPE_MAP: Record<number, ProcessingPreviewItem['type']> = {
          1: 'storyboard',
          2: 'character',
          3: 'location',
          4: 'prop',
        };

        const newTasks: GenerationTask[] = [];
        const newProcessingItems: ProcessingPreviewItem[] = [];
        const trackedImageEditJobIds = new Set(imageEditJobIds ?? []);

        for (const job of jobs) {
          if (!job.source || existingJobIds.has(job.jobId)) continue;
          const { source } = job;
          if (!source.projectId) continue;

          const modeKey = `${source.targetType}_${source.mediaType}`;
          const mode = MODE_MAP[modeKey];
          if (!mode) continue;

          const slot = SLOT_BY_MODE[mode];
          if (!slot) continue;

          const isStoryboard = source.targetType === 1;
          const storyboardId = isStoryboard ? source.targetId : null;
          const resourceId = isStoryboard ? null : source.targetId;
          const splitFields = resolveRestoredSplitTaskFields({
            jobId: job.jobId,
            source,
            trackTypeName: job.trackTypeName,
            fallbackIndex: newTasks.length + 1,
          });
          const tagId = splitFields?.tagId ?? source.tagId ?? null;
          const previewItemId = source.videoId ?? null;
          const projectId = source.projectId;
          const isImageEdit = trackedImageEditJobIds.has(job.jobId);
          const origin = splitFields?.origin ?? (isImageEdit ? ('image-edit' as const) : undefined);

          const taskId = `restored-${job.jobId}`;
          const targetKey = buildGenerationTargetKey({
            mode,
            projectId,
            storyboardId,
            resourceId,
            tagId,
            previewItemId,
          });
          const slotKey = buildSlotKey(slot, { storyboardId, resourceId });

          let parsedAspectRatio: number | undefined;
          if (job.aspectRatio) {
            const parts = job.aspectRatio.split(':').map(Number);
            if (parts.length === 2 && parts[0] > 0 && parts[1] > 0) {
              parsedAspectRatio = parts[0] / parts[1];
            }
          }

          const assetType: 'image' | 'video' = source.mediaType === 2 ? 'video' : 'image';
          const itemType: ProcessingPreviewItem['type'] =
            assetType === 'video' ? 'video' : (TYPE_MAP[source.targetType] ?? 'storyboard');

          newTasks.push({
            localTaskId: taskId,
            jobId: job.jobId,
            slot,
            slotKey,
            targetKey,
            mode,
            projectId,
            storyboardId,
            resourceId,
            tagId,
            previewItemId,
            status: 'processing',
            createdAt: new Date(job.createdAt).getTime(),
            prompt: '',
            assetType,
            previousAssetUrl: null,
            previousThumbnailUrl: null,
            progress: job.progress,
            aspectRatio: parsedAspectRatio,
            ...(origin ? { origin, affectsLimiter: false } : {}),
          });

          newProcessingItems.push({
            id: `processing-${taskId}`,
            localTaskId: taskId,
            jobId: job.jobId,
            targetKey,
            mode,
            assetType,
            projectId,
            storyboardId,
            resourceId,
            tagId,
            previewItemId,
            title: splitFields?.title ?? job.trackTypeName ?? '生成中',
            description: '',
            type: itemType,
            thumbnailUrl: '',
            status: 'processing',
            progress: job.progress,
            aspectRatio: parsedAspectRatio,
            phase: 'progress',
          });
        }

        if (newTasks.length === 0) return;

        // 恢复的任务只用于进度展示，不再占用 runningBySlot 槔位：
        // 槔位设计意图是"防止瞬时重复提交"，恢复任务往往会运行数十秒到数分钟，
        // 长期占用槔位会导致刷新页面后同分镜同类型的新提交被静默拦截。
        set((state) => ({
          tasks: [...newTasks, ...state.tasks],
          processingItems: [...newProcessingItems, ...state.processingItems],
        }));
      },
      removeProcessingByTarget: (targetKey) =>
        set((state) => ({
          processingItems: state.processingItems.filter((item) => item.targetKey !== targetKey),
        })),
      removeCurrentByTarget: (targetKey) =>
        set((state) => {
          const { [targetKey]: _removed, ...rest } = state.currentByTarget;
          void _removed;
          return { currentByTarget: rest };
        }),
    }),
    {
      name: 'GenerationStore',
    }
  )
);
